import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import BusinessIcon from '@mui/icons-material/Business';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const C = {
  green: '#1B5E20',
  greenMid: '#2e7d32',
  greenLight: '#E8F5E9',
  text: '#1E293B',
  sub: '#64748B',
  border: '#E2E8F0',
  statBg: '#F8FAFC',
  tag: '#334155',
  tagBg: '#F1F5F9',
};

const DeveloperCard = ({
  developer,
  projectCount = 0,
  availableCount = 0,
  onEdit,
  onAddProject,
  onDelete,
  onOpenProjects,
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const openMenu = (e) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); };
  const closeMenu = () => setMenuAnchor(null);

  const name = developer?.name || '—';
  const logo = developer?.logo;
  const tags = Array.isArray(developer?.tags) ? developer.tags : [];
  const canDelete = projectCount === 0;

  const handleEdit = (e) => { e?.stopPropagation(); closeMenu(); onEdit?.(developer); };
  const handleAddProject = (e) => { e?.stopPropagation(); closeMenu(); onAddProject?.(developer); };
  const handleDelete = () => { closeMenu(); if (canDelete) onDelete?.(developer); };
  const handleViewProjects = (e) => { e?.stopPropagation(); closeMenu(); onOpenProjects?.(developer); };
  const handleCardClick = () => { if (onOpenProjects) onOpenProjects(developer); };

  return (
    <Box
      onClick={handleCardClick}
      sx={{
        width: '100%',
        flex: 1,
        borderRadius: 3,
        overflow: 'hidden',
        bgcolor: '#FFFFFF',
        border: '1px solid',
        borderColor: C.border,
        cursor: onOpenProjects ? 'pointer' : 'default',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.22s cubic-bezier(.4,0,.2,1)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.03)',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: '0 10px 28px rgba(27,94,32,0.10), 0 4px 12px rgba(0,0,0,0.06)',
          borderColor: '#A5D6A7',
        },
      }}
    >
      {/* Top accent bar */}
      <Box sx={{ height: 3, background: 'linear-gradient(90deg, #1B5E20 0%, #43A047 50%, #0D9488 100%)' }} />

      {/* Header: logo + name + menu */}
      <Box sx={{ p: 2, pb: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        {logo ? (
          <Avatar
            src={logo}
            alt={name}
            variant="rounded"
            sx={{
              width: 60,
              height: 60,
              borderRadius: 2,
              border: '1.5px solid',
              borderColor: C.border,
              flexShrink: 0,
              bgcolor: '#fff',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              '& img': { objectFit: 'contain', p: 0.5 },
            }}
          />
        ) : (
          <Avatar
            variant="rounded"
            sx={{
              width: 60,
              height: 60,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)',
              color: C.green,
              flexShrink: 0,
              boxShadow: '0 2px 8px rgba(27,94,32,0.08)',
            }}
          >
            <BusinessIcon sx={{ fontSize: 28 }} />
          </Avatar>
        )}

        <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
          <Typography sx={{
            fontSize: 14.5,
            fontWeight: 700,
            color: C.text,
            lineHeight: 1.3,
            letterSpacing: -0.2,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            mb: 0.5,
          }}>
            {name}
          </Typography>
          {developer?.website && (
            <Typography
              component="span"
              sx={{
                fontSize: 11,
                color: C.sub,
                fontWeight: 500,
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {developer.website.replace(/^https?:\/\//, '')}
            </Typography>
          )}
        </Box>

        <IconButton
          size="small"
          onClick={openMenu}
          sx={{
            color: C.sub,
            flexShrink: 0,
            mt: -0.25,
            '&:hover': { bgcolor: C.greenLight },
          }}
          aria-label="Developer menu"
        >
          <MoreVertIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* Stats row */}
      <Box sx={{ px: 2, pb: 1.5, display: 'flex', gap: 1 }}>
        <Box sx={{
          flex: 1,
          borderRadius: 2,
          bgcolor: C.statBg,
          border: '1px solid',
          borderColor: C.border,
          p: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}>
          <Box sx={{
            width: 30, height: 30, borderRadius: 1.5,
            bgcolor: C.greenLight,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <FolderOpenOutlinedIcon sx={{ fontSize: 16, color: C.greenMid }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 17, fontWeight: 700, color: C.text, lineHeight: 1 }}>
              {projectCount}
            </Typography>
            <Typography sx={{ fontSize: 9.5, color: C.sub, textTransform: 'uppercase', letterSpacing: 0.4, mt: 0.25 }}>
              Projects
            </Typography>
          </Box>
        </Box>
        <Box sx={{
          flex: 1,
          borderRadius: 2,
          bgcolor: C.statBg,
          border: '1px solid',
          borderColor: C.border,
          p: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}>
          <Box sx={{
            width: 30, height: 30, borderRadius: 1.5,
            bgcolor: C.greenLight,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 16, color: C.greenMid }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 17, fontWeight: 700, color: C.text, lineHeight: 1 }}>
              {availableCount}
            </Typography>
            <Typography sx={{ fontSize: 9.5, color: C.sub, textTransform: 'uppercase', letterSpacing: 0.4, mt: 0.25 }}>
              Available
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Tags */}
      {tags.length > 0 && (
        <Box sx={{ px: 2, pb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
          <LocalOfferOutlinedIcon sx={{ fontSize: 13, color: C.sub, mr: 0.25 }} />
          {tags.slice(0, 3).map((tag) => {
            const label = typeof tag === 'string' ? tag : tag?.label ?? tag;
            return (
              <Box key={label} sx={{
                display: 'inline-flex', alignItems: 'center',
                px: 0.85, py: 0.3, borderRadius: 10,
                bgcolor: C.tagBg,
              }}>
                <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: C.tag }}>{label}</Typography>
              </Box>
            );
          })}
          {tags.length > 3 && (
            <Box
              component="button"
              type="button"
              onClick={(e) => { e.stopPropagation(); onEdit?.(developer); }}
              sx={{
                display: 'inline-flex', alignItems: 'center',
                px: 0.75, py: 0.25, borderRadius: 10,
                bgcolor: C.tagBg, border: 'none',
                cursor: 'pointer', font: 'inherit',
                '&:hover': { bgcolor: C.border },
              }}
            >
              <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: C.sub }}>
                +{tags.length - 3} more
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {tags.length === 0 && <Box sx={{ pb: 1 }} />}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 160 }, onClick: (e) => e.stopPropagation() } }}
      >
        <MenuItem onClick={handleEdit}>
          <ListItemIcon><EditOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Edit" />
        </MenuItem>
        <MenuItem onClick={handleAddProject}>
          <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Add Project" />
        </MenuItem>
        {onOpenProjects && (
          <MenuItem onClick={handleViewProjects}>
            <ListItemIcon><ChevronRightIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="View Projects" />
          </MenuItem>
        )}
        <MenuItem onClick={handleDelete} disabled={!canDelete}>
          <ListItemIcon><DeleteOutlinedIcon fontSize="small" sx={{ color: canDelete ? '#d32f2f' : undefined }} /></ListItemIcon>
          <ListItemText primary="Delete" primaryTypographyProps={{ color: canDelete ? '#d32f2f' : undefined }} />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default DeveloperCard;
